import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDiscoveryRequest } from "../../contexts/DiscoveryRequestContext";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Heading, Subheading } from "../../design/heading";
import { Divider } from "../../design/divider";
import DeleteDiscoveryRequestButton from "./components/DeleteDiscoveryRequestButton";
import LoadingView from "../../components/LoadingView";
import moment from "moment";
import EditDiscoveryRequestButton from "./components/EditDiscoveryRequestButton";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import DiscoveryRequestTasksList from "./components/DiscoveryRequestTaskList/DiscoveryRequestTaskList";
import ParticipantSideBadge from "../../components/ParticipantSideBadge";
import { Navbar, NavbarItem } from "../../design/navbar";
import FileViewer from "../../components/FileViewer";
import { DiscoveryRequestService } from "../../services/DiscoveryRequestService";

const DiscoveryRequestDetailPage: React.FC = () => {
  const { discoveryRequestId } = useParams<{ discoveryRequestId: string }>();
  const [state, setState] = React.useState<"Document" | "Tasks">("Document");
  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const {
    selectedDiscoveryRequest,
    setSelectedDiscoveryRequest,
    error,
    loading,
  } = useDiscoveryRequest();

  useEffect(() => {
    if (discoveryRequestId) {
      setSelectedDiscoveryRequest(discoveryRequestId);
    }
  }, [discoveryRequestId]);

  useEffect(() => {
    if (!discoveryRequestId) return;
    if (state !== "Document") return;
    const fetchFileUrl = async () => {
      const url = await DiscoveryRequestService.getDownloadURL(
        discoveryRequestId!,
      );
      setFileUrl(url);
    };
    fetchFileUrl();
  }, [discoveryRequestId, state]);

  if (loading.details) {
    return <LoadingView title="Loading Discovery Request" />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedDiscoveryRequest) {
    return <ErrorView error={new Error("DiscoveryRequest not found")} />;
  }

  const completedTasks = selectedDiscoveryRequest.tasks?.filter(
    (task) => task.isCompleted,
  ).length;

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Discovery Requests", href: "/discovery-requests" },
          {
            label:
              selectedDiscoveryRequest.title || selectedDiscoveryRequest?._id,
            href: `/discovery-requests/${selectedDiscoveryRequest._id}`,
          },
        ]}
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <Heading>{selectedDiscoveryRequest.title}</Heading>
              <div className="flex gap-2">
                <EditDiscoveryRequestButton
                  discoveryRequest={selectedDiscoveryRequest}
                />
                <DeleteDiscoveryRequestButton
                  discoveryRequestId={discoveryRequestId!}
                />
              </div>
            </div>
          </div>
          <DescriptionList>
            <DescriptionTerm>Side</DescriptionTerm>
            <DescriptionDetails>
              <ParticipantSideBadge side={selectedDiscoveryRequest.side} />
            </DescriptionDetails>
            <DescriptionTerm>Due Date</DescriptionTerm>
            <DescriptionDetails>
              {moment.utc(selectedDiscoveryRequest.dueDate).format("LL")}
            </DescriptionDetails>
            {selectedDiscoveryRequest.description && (
              <>
                <DescriptionTerm>Description</DescriptionTerm>
                <DescriptionDetails style={{ whiteSpace: "pre-line" }}>
                  {selectedDiscoveryRequest.description}
                </DescriptionDetails>
              </>
            )}
            <DescriptionTerm>Tasks</DescriptionTerm>
            <DescriptionDetails>
              {selectedDiscoveryRequest.tasks?.length || 0}{" "}
              {completedTasks ? `(${completedTasks} completed)` : ""}
            </DescriptionDetails>
          </DescriptionList>
          <Navbar className="mb-4">
            {(["Document", "Tasks"] as const).map((item) => (
              <NavbarItem
                key={item}
                className="text-base"
                onClick={() => setState(item)}
                current={state === item}
              >
                {item}
              </NavbarItem>
            ))}
          </Navbar>
          {state === "Document" && fileUrl && <FileViewer fileUrl={fileUrl} />}
          {state === "Tasks" && discoveryRequestId && (
            <DiscoveryRequestTasksList
              discoveryRequestId={discoveryRequestId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscoveryRequestDetailPage;
